/**
 * Placement test questions from German Placement Test Design (B1–C1).
 * Same formats as grammar exercise engine: multipleChoice, fillInTheBlanks, sentenceBuilding, errorCorrection.
 * Each has: id, type, difficulty, exerciseType, question, feedback, plus type-specific fields (options/correctAnswer, stimulus/answers, incorrectPart/correctStimulus).
 */

// Helper: blank in sentence is "______" (multiple underscores) for fill-in; grammar engine also accepts [---]
const QUESTIONS_ARRAY = [
  //, , B1 (1–5), , 
  {
    id: 'p1',
    type: 'grammar',
    difficulty: 1,
    exerciseType: 'multipleChoice',
    question: 'Complete the sentence (Passive, present).',
    stimulus: 'In dieser Bäckerei ______ jeden Morgen frische Brötchen ______.',
    options: ['wird / gebacken', 'werden / gebacken', 'wurden / backen', 'werden / gebacken haben'],
    correctAnswer: 'werden / gebacken',
    feedback: 'This item tests the present tense passive (Vorgangspassiv). "Brötchen" is plural, so the auxiliary must be "werden" (not "wird") with Partizip II "gebacken".'
  },
  {
    id: 'p2',
    type: 'grammar',
    difficulty: 1,
    exerciseType: 'fillInTheBlanks',
    question: 'Fill in the Konjunktiv II forms.',
    stimulus: 'Das Wetter ist leider sehr schlecht. Wenn die Sonne ______, ______ wir im Park spazieren gehen.',
    answers: [{ correct: 'scheinen würde' }, { correct: 'würden' }],
    feedback: 'Konjunktiv II is required for hypothetical situations. The correct pairing is "scheinen würde" (or "schiene") in the subordinate clause and "würden" in the main clause.'
  },
  {
    id: 'p3',
    type: 'grammar',
    difficulty: 1,
    exerciseType: 'sentenceBuilding',
    question: 'Build a correct sentence (subordinate clause with "obwohl", verb-final, then verb-second).',
    stimulus: ['Obwohl', 'er', 'sein', 'Auto', 'repariert', 'hat', 'funktioniert', 'es', 'immer', 'noch', 'nicht'],
    correctAnswer: 'Obwohl er sein Auto repariert hat, funktioniert es immer noch nicht.',
    feedback: 'With "obwohl", the subordinate clause has verb-final word order ("repariert hat"); the main clause then starts with the finite verb ("funktioniert"), the "sentence bracket" (Satzklammer) typical at B1/B2.'
  },
  {
    id: 'p4',
    type: 'grammar',
    difficulty: 1,
    exerciseType: 'fillInTheBlanks',
    question: 'Partizip II of "besprechen".',
    stimulus: 'Wir haben das Problem gestern mit dem Chef ______.',
    answers: [{ correct: 'besprochen' }],
    feedback: 'Partizip II of "besprechen" (inseparable prefix) is "besprochen", no "ge-" prefix. Over-regularizing to "gebesprochen" is a common B1 interlanguage error.'
  },
  {
    id: 'p5',
    type: 'grammar',
    difficulty: 1,
    exerciseType: 'errorCorrection',
    question: 'Correct the word order. "deshalb" requires verb inversion.',
    stimulus: 'Ich gehe heute nicht zur Arbeit, denn ich bin krank und deshalb ich muss zum Arzt gehen.',
    incorrectPart: 'ich muss',
    correctStimulus: 'Ich gehe heute nicht zur Arbeit, denn ich bin krank und deshalb muss ich zum Arzt gehen.',
    feedback: '"deshalb" is an adverbial connector and requires verb inversion in the main clause (muss ich). "denn" does not trigger inversion.'
  },
  //, , B2 (6–10), , 
  {
    id: 'p6',
    type: 'grammar',
    difficulty: 2,
    exerciseType: 'fillInTheBlanks',
    question: 'Two-part connectors: fill in the blanks.',
    stimulus: 'Dieser neue Job bietet ______ ein attraktives Gehalt, ______ auch sehr flexible Arbeitszeiten.',
    answers: [{ correct: 'nicht nur' }, { correct: 'sondern' }],
    feedback: 'The additive pair "nicht nur ... sondern (auch)" is required here. Two-part connectors are a key B2 indicator.'
  },
  {
    id: 'p7',
    type: 'grammar',
    difficulty: 2,
    exerciseType: 'multipleChoice',
    question: 'Choose the correct Genitiv preposition (context: despite the protests).',
    stimulus: '______ der heftigen Proteste wurde das Gesetz schließlich verabschiedet.',
    options: ['Wegen', 'Trotz', 'Während', 'Aufgrund'],
    correctAnswer: 'Trotz',
    feedback: 'The context implies "despite" the protests, so "Trotz" (with Genitive "der heftigen Proteste") is correct. At B2, Genitiv prepositions mark stylistic maturity.'
  },
  {
    id: 'p8',
    type: 'grammar',
    difficulty: 2,
    exerciseType: 'multipleChoice',
    question: 'Passive in the past: which is/are correct?',
    stimulus: 'Das Haus ______ bereits vor zwanzig Jahren ______.',
    options: [
      'wurde / gebaut',
      'ist / gebaut worden',
      'war / gebaut',
      'A und B sind korrekt (wurde gebaut / ist gebaut worden)'
    ],
    correctAnswer: 'A und B sind korrekt (wurde gebaut / ist gebaut worden)',
    feedback: 'Both Präteritum passive ("wurde gebaut") and Perfekt passive ("ist gebaut worden") are correct. This item checks mastery of the passive across tenses.'
  },
  {
    id: 'p9',
    type: 'grammar',
    difficulty: 2,
    exerciseType: 'sentenceBuilding',
    question: 'Build the correct unreal past conditional (Konjunktiv II).',
    stimulus: ['Wenn', 'ich', 'gestern', 'Zeit', 'gehabt', 'hätte', 'wäre', 'ich', 'zu', 'der', 'Party', 'gekommen'],
    correctAnswer: 'Wenn ich gestern Zeit gehabt hätte, wäre ich zu der Party gekommen.',
    feedback: 'Past unreal conditional requires "hätte" + Partizip in the if-clause and "wäre" + Partizip in the main clause. Failure here often indicates a B1 ceiling.'
  },
  {
    id: 'p10',
    type: 'grammar',
    difficulty: 2,
    exerciseType: 'errorCorrection',
    question: 'Add the correct adjective ending (Partizip II as attribute).',
    stimulus: 'Der gestern repariert Fernseher funktioniert leider schon wieder nicht.',
    incorrectPart: 'repariert',
    correctStimulus: 'Der gestern reparierte Fernseher funktioniert leider schon wieder nicht.',
    feedback: 'As an attributive adjective before a masculine noun in nominative with definite article, the participle needs the weak ending "-e": "reparierte".'
  },
  //, , C1 (11–15), , 
  {
    id: 'p11',
    type: 'grammar',
    difficulty: 3,
    exerciseType: 'multipleChoice',
    question: 'Indirect speech: Konjunktiv I (formal reporting).',
    stimulus: 'In den Nachrichten hieß es, der Präsident ______ mit den Ergebnissen der Wahl sehr zufrieden ______.',
    options: ['wäre / gewesen', 'sei / gewesen', 'ist / gewesen', 'sei / sein'],
    correctAnswer: 'sei / gewesen',
    feedback: 'Indirect speech in formal/journalistic style uses Konjunktiv I: "sei" (from "sein") + "gewesen". Konjunktiv II ("wäre") is common in speech but less precise for C1.'
  },
  {
    id: 'p12',
    type: 'grammar',
    difficulty: 3,
    exerciseType: 'multipleChoice',
    question: 'Which sentence is in correct Nominalstil?',
    stimulus: null,
    options: [
      'Die Erhöhung der Steuern führte zu Protesten.',
      'Die Regierung hatte die Steuern erhöht, was zu Protesten führte.',
      'Sie erhöhten die Steuern und das führte zu Protesten.',
      'Weil die Steuern erhöht wurden, gab es Protesten.'
    ],
    correctAnswer: 'Die Erhöhung der Steuern führte zu Protesten.',
    feedback: 'Nominalstil uses nouns (Erhöhung) instead of verbs (erhöhen) to pack information densely. Central to C1 and TestDaF-style academic German.'
  },
  {
    id: 'p13',
    type: 'grammar',
    difficulty: 3,
    exerciseType: 'multipleChoice',
    question: 'Is there a grammatical error? (Passiversatzformen)',
    stimulus: 'Die komplexe Theorie ist nicht leicht zu verstehen, aber sie lässt sich durch Beispiele anschaulicher machen.',
    options: [
      'No error. Both "ist ... zu verstehen" and "lässt sich ... machen" are correct passive alternatives.',
      '"ist ... zu verstehen" is wrong; it should be "kann verstanden werden".',
      '"lässt sich ... machen" is wrong; it should be "wird gemacht".',
      'Both alternatives are wrong; only "werden"-passive is correct.'
    ],
    correctAnswer: 'No error. Both "ist ... zu verstehen" and "lässt sich ... machen" are correct passive alternatives.',
    feedback: 'Passiversatzformen (sein + zu + Infinitiv, sich lassen + Infinitiv) are correct and typical of C1. "Correcting" them to standard "werden"-passive would show a lack of C1 stylistic variety.'
  },
  {
    id: 'p14',
    type: 'grammar',
    difficulty: 3,
    exerciseType: 'fillInTheBlanks',
    question: 'Partizip I as attributive adjective (infinitive + -d, then correct ending).',
    stimulus: 'Die in der Sonne ______ Fassade des Gebäudes beeindruckte alle Passanten.',
    answers: [{ correct: 'glänzende' }],
    feedback: 'Partizip I is "glänzend" (infinitive + -d). As attributive adjective for feminine nominative with article, it takes the ending "-e": "die glänzende Fassade".'
  },
  {
    id: 'p15',
    type: 'styling',
    difficulty: 3,
    exerciseType: 'multipleChoice',
    question: 'Welcher Satz ist in einem formellen Geschäftsbrief am angemessensten?',
    stimulus: null,
    options: [
      'Ich wollte mal fragen, ob wir den Termin schieben können.',
      'Könnten wir das Meeting vielleicht auf nächste Woche verlegen?',
      'Ich darf Sie höflichst um eine Verschiebung unseres Termins ersuchen.',
      'Sagen Sie mir Bescheid, wenn der Termin nicht passt.'
    ],
    correctAnswer: 'Ich darf Sie höflichst um eine Verschiebung unseres Termins ersuchen.',
    feedback: '(C) uses high-level formal register: "ersuchen" and formal "Sie". (B) is polite and correct at B2; (C) matches C1 business and sociolinguistic competence.'
  }
];

export const PLACEMENT_QUESTIONS = QUESTIONS_ARRAY;

/** Placement level from score. */
export function getPlacementLevel(score, totalAnswered = 15) {
  if (totalAnswered <= 0) return 'B1';
  const pct = score / totalAnswered;
  if (pct >= 0.8) return 'C1';
  if (pct >= 0.53) return 'B2';
  return 'B1';
}

export const FIRST_ADVANCED_QUESTION_INDEX = (() => {
  const i = QUESTIONS_ARRAY.findIndex((q) => q.difficulty >= 4);
  return i >= 0 ? i : 10;
})();

/** For MC, derive correctIndex from correctAnswer. */
export function getCorrectIndex(question) {
  if (question.exerciseType === 'multipleChoice' && question.options && question.correctAnswer != null) {
    const idx = question.options.findIndex((o) => o === question.correctAnswer || String(o).trim() === String(question.correctAnswer).trim());
    return idx >= 0 ? idx : 0;
  }
  return 0;
}

/** Weakest subject from answers. */
export function getWeakestSubject(questions, answers) {
  const byType = { grammar: { correct: 0, total: 0 }, styling: { correct: 0, total: 0 }, listening: { correct: 0, total: 0 } };
  questions.forEach((q, i) => {
    if (answers[i] == null) return;
    if (q.type in byType) {
      byType[q.type].total += 1;
      const correctIdx = getCorrectIndex(q);
      const userCorrect = q.exerciseType === 'multipleChoice' ? answers[i] === correctIdx : answers[i] === true;
      if (userCorrect) byType[q.type].correct += 1;
    }
  });
  let weakest = 'grammar';
  let lowestRate = 1;
  Object.entries(byType).forEach(([type, { correct, total }]) => {
    if (total === 0) return;
    const rate = correct / total;
    if (rate < lowestRate) {
      lowestRate = rate;
      weakest = type;
    }
  });
  const labels = { grammar: 'Grammar', styling: 'Styling & Register', listening: 'Hören (Listening)' };
  return labels[weakest] || 'Grammar';
}
